import { ChevronDown, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { adminClient, type AdminSkill, type AdminSkillsPage, type StudioAudience } from '@/api/adminClient';
import { MarkdownEditor, MarkdownPreview } from '@/components/markdown/MarkdownEditor';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadError } from '@/components/ui/load-state';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';

import { errorMessage, relativeTime } from '../IntegrationBuilder/builderModel';

import { useSkillAssist } from './SkillAssist';
import {
  SKILL_PAGE_SIZE,
  type SkillFilter,
  type SkillsListQuery,
  nextSkillsQuery,
  skillsEmptyLabel,
  skillsQueryFrom,
  skillsQueryParams,
} from './skillsListQuery';

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,47}$/;
const DESCRIPTION_MAX = 300;
const BODY_MAX = 20_000;

type Draft = { name: string; description: string; body: string; enabled: boolean; audience: StudioAudience };

const FILTER_OPTIONS: Array<{ id: SkillFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Published' },
  { id: 'retired', label: 'Retired' },
  { id: 'internal', label: 'Internal' },
];

const AUDIENCES: Array<{ id: StudioAudience; label: string; hint: string }> = [
  { id: 'everyone', label: 'Everyone', hint: 'Every organization can pick it.' },
  { id: 'internal', label: 'Internal', hint: 'Only people with Internal Access see it, and only in RapidSOS organizations.' },
];

function toSkillName(value: string): string {
  return value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 48);
}

/**
 * Global skills: markdown instructions organizations can pick in the
 * composer's + menu or with /skill in the CLI. Skill curators and platform
 * admins create, publish, set the audience of and retire them here; an
 * internal one is offered only to people with Internal Access in
 * RapidSOS organizations. Organizations add their own in Studio → Settings →
 * Skills.
 */
export function SkillsPage() {
  const [params, setParams] = useSearchParams();
  const query = useMemo(() => skillsQueryFrom(params), [params]);
  const [search, setSearch] = useState(query.q);
  const debounced = useDebouncedValue(search.trim());
  const [page, setPage] = useState<AdminSkillsPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [editing, setEditing] = useState<'new' | AdminSkill | null>(null);
  const request = useRef(0);

  const update = useCallback(
    (change: Partial<SkillsListQuery>) => setParams(skillsQueryParams(nextSkillsQuery(query, change)), { replace: true }),
    [query, setParams],
  );

  const lastSearch = useRef(debounced);
  useEffect(() => {
    if (lastSearch.current === debounced) return;
    lastSearch.current = debounced;
    if (debounced !== query.q) update({ q: debounced });
  }, [debounced, query.q, update]);

  const load = useCallback(async () => {
    const id = ++request.current;
    setLoading(true);
    setError(undefined);
    try {
      const result = await adminClient.listSkills({ q: query.q, filter: query.filter, offset: query.offset, limit: SKILL_PAGE_SIZE });
      if (id !== request.current) return;
      if (result.items.length === 0 && result.total > 0 && query.offset > 0) {
        update({ offset: Math.max(0, Math.floor((result.total - 1) / SKILL_PAGE_SIZE) * SKILL_PAGE_SIZE) });
        return;
      }
      setPage(result);
    } catch (failure) {
      if (id === request.current) setError(errorMessage(failure));
    } finally {
      if (id === request.current) setLoading(false);
    }
  }, [query, update]);

  useEffect(() => {
    void load();
  }, [load]);

  const setPublished = async (skill: AdminSkill, enabled: boolean) => {
    try {
      await adminClient.updateSkill(skill.skillId, { enabled });
      toast.success(enabled ? `Published ${skill.name}` : `Retired ${skill.name}`);
      await load();
    } catch (failure) {
      toast.error(errorMessage(failure));
    }
  };

  const remove = async (skill: AdminSkill) => {
    if (!window.confirm(`Delete the global skill ${skill.name}? Organizations can no longer pick it; messages already sent are unaffected.`)) return;
    try {
      await adminClient.deleteSkill(skill.skillId);
      toast.success(`Deleted ${skill.name}`);
      await load();
    } catch (failure) {
      toast.error(errorMessage(failure));
    }
  };

  if (editing) {
    return (
      <div className={PAGE_CONTAINER_CLASS}>
        <SkillEditor
          skill={editing === 'new' ? undefined : editing}
          onDone={async (saved) => {
            setEditing(null);
            if (saved) await load();
          }}
        />
      </div>
    );
  }

  const skills = page?.items;
  const counts = page && !loading ? page.counts : undefined;

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Skills"
        description="Instructions the building agent follows when someone picks a skill. Published global skills are offered to every organization (internal ones only to Internal Access in RapidSOS organizations); organizations add their own in Studio → Settings → Skills."
        actions={
          <Button type="button" size="sm" onClick={() => setEditing('new')}>
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            New global skill
          </Button>
        }
      />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, description or id" label="Search skills" />
          <SegmentedFilter<SkillFilter>
            label="Show"
            value={query.filter}
            onChange={(filter) => update({ filter })}
            options={FILTER_OPTIONS.map((option) => ({ ...option, count: counts?.[option.id] }))}
          />
        </div>
        {error && !skills ? <LoadError message={error} onRetry={() => void load()} className="rounded-xl border border-border" /> : null}
        {error && skills ? <div className="rounded-xl border border-destructive-border bg-destructive-bg p-4 text-sm text-destructive-foreground">{error}</div> : null}
        {!skills && !error ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading skills…
          </div>
        ) : null}
        {skills && skills.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
            {skillsEmptyLabel(query)}
          </div>
        ) : null}
        {skills && skills.length > 0 ? (
          <div className={cn('space-y-2', loading && 'opacity-60')} aria-busy={loading}>
            <ul className="space-y-2">
              {skills.map((skill) => (
                <SkillRow
                  key={skill.skillId}
                  skill={skill}
                  onEdit={() => setEditing(skill)}
                  onTogglePublished={() => void setPublished(skill, !skill.enabled)}
                  onDelete={() => void remove(skill)}
                />
              ))}
            </ul>
            <TablePager
              total={page?.total ?? 0}
              limit={page?.limit ?? SKILL_PAGE_SIZE}
              offset={query.offset}
              onOffsetChange={(offset) => update({ offset })}
              disabled={loading}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SkillRow({
  skill,
  onEdit,
  onTogglePublished,
  onDelete,
}: {
  skill: AdminSkill;
  onEdit: () => void;
  onTogglePublished: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-xl border border-border bg-card">
      <div className="flex items-start gap-3 px-4 py-3">
        <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)} className="flex min-w-0 flex-1 items-start gap-2 text-left">
          <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')} aria-hidden="true" />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-medium text-foreground">{skill.name}</span>
              {!skill.enabled ? (
                <span className="rounded-full border border-border px-2 py-px text-[10px] uppercase tracking-wide text-muted-foreground">Retired</span>
              ) : null}
              {skill.audience === 'internal' ? (
                <span
                  title="Only people with Internal Access see it, in RapidSOS organizations."
                  className="rounded-full border border-amber-500/40 px-2 py-px text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300"
                >
                  Internal
                </span>
              ) : null}
              {skill.updatedAt ? <span className="text-[11px] text-muted-foreground">updated {relativeTime(skill.updatedAt)}</span> : null}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">{skill.description}</span>
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onTogglePublished}>
            {skill.enabled ? 'Retire' : 'Publish'}
          </Button>
          <Button type="button" variant="ghost" size="sm" aria-label={`Delete ${skill.name}`} onClick={onDelete}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {open ? (
        <div className="border-t border-border px-5 py-4">
          <MarkdownPreview markdown={skill.body} />
        </div>
      ) : null}
    </li>
  );
}

function SkillEditor({ skill, onDone }: { skill?: AdminSkill; onDone: (saved: boolean) => void | Promise<void> }) {
  const [draft, setDraft] = useState<Draft>(
    skill
      ? { name: skill.name, description: skill.description, body: skill.body, enabled: skill.enabled, audience: skill.audience ?? 'everyone' }
      : { name: '', description: '', body: '', enabled: true, audience: 'everyone' },
  );
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const assist = useSkillAssist({
    name: draft.name,
    description: draft.description,
    body: draft.body,
    selection,
    onChangeBody: (body) => setDraft((current) => ({ ...current, body })),
    request: async (input) => (await adminClient.assistSkill(input)).text,
  });
  const nameValid = NAME_PATTERN.test(draft.name);
  const canSave = nameValid && draft.description.trim().length > 0 && draft.body.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const input = { name: draft.name, description: draft.description.trim(), body: draft.body, enabled: draft.enabled, audience: draft.audience };
      if (skill) await adminClient.updateSkill(skill.skillId, input);
      else await adminClient.createSkill(input);
      toast.success(`Saved ${draft.name}`);
      await onDone(true);
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        title={skill ? `Edit ${skill.name}` : 'New global skill'}
        description="The agent reads the name and description to know what the skill is for, and follows the instructions on every change that uses it."
        actions={
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => void onDone(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={() => void save()} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="grid gap-5">
        <div className="grid gap-1.5">
          <Label htmlFor="skill-name">Name</Label>
          <Input
            id="skill-name"
            value={draft.name}
            onChange={(event) => setDraft((current) => ({ ...current, name: toSkillName(event.target.value) }))}
            placeholder="accessibility"
            className="max-w-sm font-mono"
            aria-invalid={draft.name.length > 0 && !nameValid}
          />
          <p className="text-xs text-muted-foreground">Lowercase letters, digits and dashes. Picked in the composer and typed after /skill in the CLI.</p>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="skill-description">When to use it</Label>
          <Input
            id="skill-description"
            value={draft.description}
            maxLength={DESCRIPTION_MAX}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            placeholder="For example: Screens that must meet WCAG 2.2 AA"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="skill-body">Instructions</Label>
          <MarkdownEditor
            id="skill-body"
            value={draft.body}
            onChange={(body) => setDraft((current) => ({ ...current, body }))}
            maxLength={BODY_MAX}
            placeholder={'## What to do\n\n- …'}
            minRows={18}
            onSelectionChange={setSelection}
            toolbarEnd={assist.toolbarButton}
          />
          {assist.panel}
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => setDraft((current) => ({ ...current, enabled: event.target.checked }))}
            className="h-4 w-4 rounded border-border"
          />
          Published: offer this skill to organizations (clear to retire it)
        </label>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium text-foreground">Audience</legend>
          {AUDIENCES.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="skill-audience"
                value={option.id}
                checked={draft.audience === option.id}
                onChange={() => setDraft((current) => ({ ...current, audience: option.id }))}
                className="mt-0.5 h-4 w-4 cursor-pointer"
              />
              <span>
                {option.label}
                <span className="block text-xs text-muted-foreground">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </div>
    </>
  );
}
